import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import {
  Email as EmailIcon,
  Lock as LockIcon,
  Person as PersonIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { useFormik } from 'formik';
import { object, string, ref as yupRef } from 'yup';
import {
  changeConsignorPassword,
  fetchConsignorProfile,
  updateConsignorProfile,
} from '../api/consignorProfile';
import {
  DEFAULT_DIAL_CODE,
  formatE164,
  isValidNationalPhone,
  splitStoredPhone,
} from '../constants/phoneDialCodes';
import PhoneDialField from './PhoneDialField';

// ─── Style helpers ────────────────────────────────────────────────────────────

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: 'rgba(248, 250, 252, 1)',
    '& fieldset': { borderColor: 'rgba(219, 224, 230, 1)' },
    '&:hover fieldset': { borderColor: 'rgba(148, 163, 184, 0.7)' },
    '&.Mui-focused fieldset': { borderColor: '#111827' },
  },
};

const sectionLabelSx = {
  fontSize: 11,
  fontWeight: 700,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.08em',
  color: 'text.disabled',
  mb: 1.5,
};

// ─── Avatar ───────────────────────────────────────────────────────────────────

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// ─── Validation schemas ───────────────────────────────────────────────────────

const profileSchema = object({
  name: string().trim().required('El nombre es obligatorio'),
  email: string().trim().email('Correo inválido').required('El correo es obligatorio'),
  dialCode: string().required('Selecciona un código de país'),
  phone: string()
    .trim()
    .test('phone-optional', 'Ingresa un teléfono válido', (value, ctx) => {
      if (!value) return true;
      return isValidNationalPhone(ctx.parent.dialCode, value);
    }),
});

const passwordSchema = object({
  currentPassword: string().required('Ingresa tu contraseña actual'),
  newPassword: string().min(8, 'Mínimo 8 caracteres').required('Ingresa una nueva contraseña'),
  confirmPassword: string()
    .required('Confirma tu nueva contraseña')
    .oneOf([yupRef('newPassword')], 'Las contraseñas no coinciden'),
});

// ─── Password field ───────────────────────────────────────────────────────────

function PasswordField({
  label,
  name,
  value,
  onChange,
  onBlur,
  error,
  helperText,
  disabled,
}: {
  label: string;
  name: string;
  value: string;
  onChange: React.ChangeEventHandler<HTMLInputElement>;
  onBlur: React.FocusEventHandler<HTMLInputElement>;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
}): React.ReactElement {
  const [show, setShow] = useState(false);
  return (
    <TextField
      label={label}
      name={name}
      type={show ? 'text' : 'password'}
      fullWidth
      size="small"
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      error={error}
      helperText={helperText}
      disabled={disabled}
      sx={fieldSx}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <LockIcon fontSize="small" sx={{ color: 'text.disabled' }} />
          </InputAdornment>
        ),
        endAdornment: (
          <InputAdornment position="end">
            <IconButton
              size="small"
              onClick={() => setShow((s) => !s)}
              edge="end"
              aria-label={show ? 'Ocultar contraseña' : 'Mostrar contraseña'}
            >
              {show ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
  );
}

PasswordField.defaultProps = {
  error: false,
  helperText: undefined,
  disabled: false,
};

// ─── Main page ────────────────────────────────────────────────────────────────

export default function ConsignorProfilePage(): React.ReactElement {
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [snackMessage, setSnackMessage] = useState<string | null>(null);
  const [profileSaveError, setProfileSaveError] = useState<string | null>(null);
  const [passwordSaveError, setPasswordSaveError] = useState<string | null>(null);

  const profileFormik = useFormik({
    initialValues: { name: '', email: '', dialCode: DEFAULT_DIAL_CODE, phone: '' },
    validationSchema: profileSchema,
    onSubmit: async (values, helpers) => {
      setProfileSaveError(null);
      const result = await updateConsignorProfile({
        name: values.name.trim(),
        email: values.email.trim(),
        phone: values.phone.trim() ? formatE164(values.dialCode, values.phone) : null,
      });
      if (!result.ok) {
        setProfileSaveError(result.error);
        helpers.setSubmitting(false);
        return;
      }
      setSnackMessage('Perfil actualizado correctamente.');
      helpers.setSubmitting(false);
    },
  });

  const passwordFormik = useFormik({
    initialValues: { currentPassword: '', newPassword: '', confirmPassword: '' },
    validationSchema: passwordSchema,
    onSubmit: async (values, helpers) => {
      setPasswordSaveError(null);
      const result = await changeConsignorPassword({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      });
      if (!result.ok) {
        setPasswordSaveError(result.error);
        helpers.setSubmitting(false);
        return;
      }
      setSnackMessage('Contraseña actualizada correctamente.');
      helpers.resetForm();
    },
  });

  useEffect(() => {
    let cancelled = false;
    setLoadingProfile(true);
    fetchConsignorProfile().then((result) => {
      if (cancelled) return;
      setLoadingProfile(false);
      if (!result.ok) {
        setLoadError(result.error);
        return;
      }
      const parts = splitStoredPhone(result.data.phone);
      profileFormik.resetForm({
        values: {
          name: result.data.name,
          email: result.data.email,
          dialCode: parts.dialCode,
          phone: parts.national,
        },
      });
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const displayName = profileFormik.values.name;

  return (
    <Box sx={{ maxWidth: 480, mx: 'auto', px: 2, pt: 3, pb: 12 }}>
      {/* ── Avatar + name ── */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 4 }}>
        <Box
          sx={{
            width: 72,
            height: 72,
            borderRadius: '50%',
            bgcolor: '#111827',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontWeight: 800,
            fontSize: 22,
            letterSpacing: 1,
            mb: 1.5,
            userSelect: 'none',
          }}
        >
          {loadingProfile ? '…' : initials(displayName || '?')}
        </Box>
        {!loadingProfile && (
          <Typography variant="h6" fontWeight={800} textAlign="center">
            {displayName || 'Mi perfil'}
          </Typography>
        )}
      </Box>

      {loadError && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {loadError}
        </Alert>
      )}

      {loadingProfile ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={28} sx={{ color: '#111827' }} />
        </Box>
      ) : (
        <>
          {/* ── Info section ── */}
          <Typography sx={sectionLabelSx}>Información de cuenta</Typography>

          <Box
            component="form"
            noValidate
            onSubmit={profileFormik.handleSubmit}
            sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}
          >
            <TextField
              label="Nombre completo"
              name="name"
              fullWidth
              size="small"
              value={profileFormik.values.name}
              onChange={profileFormik.handleChange}
              onBlur={profileFormik.handleBlur}
              error={profileFormik.touched.name && Boolean(profileFormik.errors.name)}
              helperText={profileFormik.touched.name && profileFormik.errors.name}
              disabled={profileFormik.isSubmitting}
              sx={fieldSx}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              label="Correo electrónico"
              name="email"
              type="email"
              fullWidth
              size="small"
              value={profileFormik.values.email}
              onChange={profileFormik.handleChange}
              onBlur={profileFormik.handleBlur}
              error={profileFormik.touched.email && Boolean(profileFormik.errors.email)}
              helperText={profileFormik.touched.email && profileFormik.errors.email}
              disabled={profileFormik.isSubmitting}
              sx={fieldSx}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                  </InputAdornment>
                ),
              }}
            />

            <PhoneDialField
              id="profile-phone"
              label="Teléfono"
              compact
              dialCode={profileFormik.values.dialCode}
              national={profileFormik.values.phone}
              onDialCodeChange={(code) => profileFormik.setFieldValue('dialCode', code)}
              onNationalChange={(digits) => profileFormik.setFieldValue('phone', digits)}
              onBlur={() => profileFormik.setFieldTouched('phone', true)}
              error={Boolean(profileFormik.touched.phone && profileFormik.errors.phone)}
              helperText={
                profileFormik.touched.phone && profileFormik.errors.phone
                  ? profileFormik.errors.phone
                  : 'Opcional — México +52 por defecto'
              }
              disabled={profileFormik.isSubmitting}
            />

            {profileSaveError && <Alert severity="error">{profileSaveError}</Alert>}

            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={profileFormik.isSubmitting}
              startIcon={
                profileFormik.isSubmitting ? <CircularProgress size={16} color="inherit" /> : null
              }
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                borderRadius: 2,
                py: 1.4,
                bgcolor: '#111827',
                '&:hover': { bgcolor: '#374151' },
              }}
            >
              {profileFormik.isSubmitting ? 'Guardando…' : 'Guardar cambios'}
            </Button>
          </Box>

          <Divider sx={{ mb: 3 }} />

          {/* ── Password section ── */}
          <Typography sx={sectionLabelSx}>Cambiar contraseña</Typography>

          <Box
            component="form"
            noValidate
            onSubmit={passwordFormik.handleSubmit}
            sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
          >
            <PasswordField
              label="Contraseña actual"
              name="currentPassword"
              value={passwordFormik.values.currentPassword}
              onChange={passwordFormik.handleChange}
              onBlur={passwordFormik.handleBlur}
              error={
                passwordFormik.touched.currentPassword &&
                Boolean(passwordFormik.errors.currentPassword)
              }
              helperText={
                passwordFormik.touched.currentPassword
                  ? passwordFormik.errors.currentPassword
                  : undefined
              }
              disabled={passwordFormik.isSubmitting}
            />

            <PasswordField
              label="Nueva contraseña"
              name="newPassword"
              value={passwordFormik.values.newPassword}
              onChange={passwordFormik.handleChange}
              onBlur={passwordFormik.handleBlur}
              error={
                passwordFormik.touched.newPassword && Boolean(passwordFormik.errors.newPassword)
              }
              helperText={
                passwordFormik.touched.newPassword
                  ? passwordFormik.errors.newPassword
                  : 'Mínimo 8 caracteres'
              }
              disabled={passwordFormik.isSubmitting}
            />

            <PasswordField
              label="Confirmar nueva contraseña"
              name="confirmPassword"
              value={passwordFormik.values.confirmPassword}
              onChange={passwordFormik.handleChange}
              onBlur={passwordFormik.handleBlur}
              error={
                passwordFormik.touched.confirmPassword &&
                Boolean(passwordFormik.errors.confirmPassword)
              }
              helperText={
                passwordFormik.touched.confirmPassword
                  ? passwordFormik.errors.confirmPassword
                  : undefined
              }
              disabled={passwordFormik.isSubmitting}
            />

            {passwordSaveError && <Alert severity="error">{passwordSaveError}</Alert>}

            <Button
              type="submit"
              variant="outlined"
              fullWidth
              disabled={passwordFormik.isSubmitting}
              startIcon={
                passwordFormik.isSubmitting ? <CircularProgress size={16} color="inherit" /> : null
              }
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                borderRadius: 2,
                py: 1.4,
                borderColor: '#111827',
                color: '#111827',
                '&:hover': { bgcolor: 'rgba(17,24,39,0.05)', borderColor: '#111827' },
              }}
            >
              {passwordFormik.isSubmitting ? 'Actualizando…' : 'Cambiar contraseña'}
            </Button>
          </Box>
        </>
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackMessage != null}
        autoHideDuration={4000}
        onClose={() => setSnackMessage(null)}
        message={snackMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}
